/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Stack } from '@fluentui/react/lib/Stack';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { logTileCardClick } from 'src/core/services/telemetry/eventsNamesTemplate';
import { TelemetryFeatureTitle } from 'src/core/types/telemetryTypes';
import { isDevelopmentEnvironment, isStagingEnvironment } from 'src/core/utils/api/environment';
import { getAssetsUrl } from 'src/core/utils/api/getAssetsUrl';
import { routeTable } from 'src/core/utils/routeTable';
import { PrebuiltDemoCard, PrebuiltDemoCardProps } from 'src/languageStudio/appShell/landingPage/components/PrebuiltDemoCard';
import { customTranslationDemoLink } from 'src/languageStudio/appShell/landingPage/utils/landingPageLinks';
import { getImageSrc } from 'src/languageStudio/appShell/landingPage/utils/landingPageUtils';
import { DemoId } from 'src/languageStudio/core/utils/demosUtils';
import { AzureResourcesManager } from 'src/modules/azure/components/AzureResourcesManager';
import { AzureResourcesStrategies } from 'src/modules/azure/utils/resourcesStrategies';

type PrebuiltDemosSectionProps = { id?: string; demosToDisplay?: DemoId[]; demoTitle?: TelemetryFeatureTitle };

const messages = defineMessages({
    piiDemoName: { id: 'languageStudio.prebuiltDemosSection.piiDemoName', defaultMessage: 'Extract PII' },
    qnaDemoName: { id: 'languageStudio.prebuiltDemosSection.qnaDemoName', defaultMessage: 'Answer questions' },
    keyPhrasesDemoName: { id: 'languageStudio.prebuiltDemosSection.keyPhrasesDemoName', defaultMessage: 'Extract key phrases' },
    detectLanguageDemoName: { id: 'languageStudio.prebuiltDemosSection.detectLanguageDemoName', defaultMessage: 'Detect language' },
    textTranslationDemoName: { id: 'languageStudio.prebuiltDemosSection.textTranslationDemoName', defaultMessage: 'Translate text' },
    summarizationDemoName: { id: 'languageStudio.prebuiltDemosSection.summarizationDemoName', defaultMessage: 'Summarize information' },
    generateQuestionsDemoName: { id: 'languageStudio.prebuiltDemosSection.generateQuestionsDemoName', defaultMessage: 'Q&A generator' },
    linkedEntitiesDemoName: { id: 'languageStudio.prebuiltDemosSection.linkedEntitiesDemoName', defaultMessage: 'Find linked entities' },
    taForHealthCardTitle: { id: 'languageStudio.prebuiltDemosSection.taForHealthCardTitle', defaultMessage: 'Extract health information ' },
    callCenterCardLink: { id: 'languageStudio.prebuiltDemosSection.callCenterCardLink', defaultMessage: 'Try out post call transcription' },
    callCenterDemoName: {
        id: 'languageStudio.prebuiltDemosSection.callCenterDemoName',
        defaultMessage: 'Post call transcription and analytics'
    },
    textTranslationDemoDescription: {
        id: 'languageStudio.prebuiltDemosSection.textTranslationDemoDescription',
        defaultMessage: 'Integrate multi-language support for text translation in your applications, websites, tools and solutions.'
    },
    identifyNamedEntitiesDemoName: {
        id: 'languageStudio.prebuiltDemosSection.identifyNamedEntitiesDemoName',
        defaultMessage: 'Extract named entities'
    },
    identifyNamedEntitiesDemoDescription: {
        id: 'languageStudio.prebuiltDemosSection.identifyNamedEntitiesDemoDescription',
        defaultMessage: 'Identify different entities in text and categorize them into pre-defined types'
    },
    sentimentDemoName: {
        id: 'languageStudio.prebuiltDemosSection.sentimentDemoName',
        defaultMessage: 'Analyze sentiment and mine opinions'
    },
    sentimentDemoDescription: {
        id: 'languageStudio.prebuiltDemosSection.sentimentDemoDescription',
        defaultMessage: 'Detect positive, negative and neutral sentiment in text. Get more insights by mining opinions.'
    },
    detectLanguageDemoDescription: {
        id: 'languageStudio.prebuiltDemosSection.detectLanguageDemoDescription',
        defaultMessage: 'Evaluate text and detect a wide range of languages and variant dialects.'
    },
    qnaDemoDescription: {
        id: 'languageStudio.prebuiltDemosSection.qnaDemoDescription',
        defaultMessage: 'Use the prebuilt question answering API to get answers to questions over unstructured text.'
    },
    generateQuestionsDemoDescription: {
        id: 'languageStudio.prebuiltDemosSection.generateQuestionsDemoDescription',
        defaultMessage: 'Generate question and answer pairs on your URLs and documents using AI language generation models.'
    },
    piiDemoDescription: {
        id: 'languageStudio.prebuiltDemosSection.piiDemoDescription',
        defaultMessage: 'Identify sensitive entities in text that are associated with an individual'
    },
    keyPhrasesDemoDescription: {
        id: 'languageStudio.prebuiltDemosSection.keyPhrasesDemoDescription',
        defaultMessage: 'Identify the most important points in a piece of text'
    },
    linkedEntitiesDemoDescription: {
        id: 'languageStudio.prebuiltDemosSection.linkedEntitiesDemoDescription',
        defaultMessage: 'Identify and disambiguate the identity of an entity found in text'
    },
    customEntityExtractionCardTitle: {
        id: 'languageStudio.prebuiltDemosSection.customEntityExtractionCardTitle',
        defaultMessage: 'Custom named entity recognition'
    },
    customEntityExtractionCardDescription: {
        id: 'languageStudio.prebuiltDemosSection.customEntityExtractionCardDescription',
        defaultMessage: 'Train an extraction model to identify your domain categories using your own data.'
    },
    customEntityExtractionCardLink: {
        id: 'languageStudio.prebuiltDemosSection.customEntityExtractionCardLink',
        defaultMessage: 'Open custom named entity recognition'
    },
    customHealthCareCardTitle: {
        id: 'languageStudio.prebuiltDemosSection.customHealthCareCardTitle',
        defaultMessage: 'Custom Text Analytics for health'
    },
    customHealthCareCardDescription: {
        id: 'languageStudio.prebuiltDemosSection.customHealthCareCardDescription',
        defaultMessage:
            'Train a healthcare extraction model that extends Text Analytics for health using your own data to identify your domain categories.'
    },
    customHealthCareCardLink: {
        id: 'languageStudio.prebuiltDemosSection.customHealthCareCardLink',
        defaultMessage: 'Open custom text analytics for health'
    },
    customClassificationCardTitle: {
        id: 'languageStudio.prebuiltDemosSection.customClassificationCardTitle',
        defaultMessage: 'Custom text classification'
    },
    customClassificationCardDescription: {
        id: 'languageStudio.prebuiltDemosSection.customClassificationCardDescription',
        defaultMessage: 'Train a classification model to classify text using your own data.'
    },
    customClassificationCardLink: {
        id: 'languageStudio.prebuiltDemosSection.customClassificationCardLink',
        defaultMessage: 'Open Custom text classification'
    },
    customConversationCardTitle: {
        id: 'languageStudio.prebuiltDemosSection.customConversationCardTitle',
        defaultMessage: 'Conversational language understanding'
    },
    customOrchestrationCardTitle: {
        id: 'languageStudio.prebuiltDemosSection.customOrchestrationCardTitle',
        defaultMessage: 'Orchestration workflow'
    },
    customOrchestrationCardDescription: {
        id: 'languageStudio.prebuiltDemosSection.customOrchestrationCardDescription',
        defaultMessage: 'Connect and orchestrate CLU, Custom question answering & LUIS projects together in one single project.'
    },
    customOrchestrationCardLink: {
        id: 'languageStudio.prebuiltDemosSection.customOrchestrationCardLink',
        defaultMessage: 'Open Orchestration workflow'
    },
    customConversationCardDescription: {
        id: 'languageStudio.prebuiltDemosSection.customConversationCardDescription',
        defaultMessage:
            'Classify utterances into intents and extract information with entities to build natural language into apps, bots, and IoT devices.'
    },
    customConversationCardLink: {
        id: 'languageStudio.prebuiltDemosSection.customConversationCardLink',
        defaultMessage: 'Open Conversational language understanding'
    },
    customQuestionsAnsweringCardTitle: {
        id: 'languageStudio.prebuiltDemosSection.customQuestionsAnsweringCardTitle',
        defaultMessage: 'Custom question answering'
    },
    customQuestionsAnsweringCardSubtitle: {
        id: 'languageStudio.prebuiltDemosSection.customQuestionsAnsweringCardSubtitle',
        defaultMessage: 'Next generation of QnAMaker'
    },
    customConversationCardSubtitle: {
        id: 'languageStudio.prebuiltDemosSection.customConversationCardSubtitle',
        defaultMessage: 'Next generation of LUIS'
    },
    customQuestionsAnsweringCardDescription: {
        id: 'languageStudio.prebuiltDemosSection.customQuestionsAnsweringCardDescription',
        defaultMessage: `Customize the list of questions and answers extracted from your content
        corpus to provide a conversational experience that suits your needs.`
    },
    customQuestionsAnsweringCardLink: {
        id: 'languageStudio.prebuiltDemosSection.customQuestionsAnsweringCardLink',
        defaultMessage: 'Open Custom question answering'
    },
    customTranslationCardTitle: {
        id: 'languageStudio.prebuiltDemosSection.customTranslationCardTitle',
        defaultMessage: 'Customize translation'
    },
    customTranslationCardDescription: {
        id: 'languageStudio.prebuiltDemosSection.customTranslationCardDescription',
        defaultMessage: 'Build neural translation systems that understand the terminology used in your own business and industry.'
    },
    customTranslationCardLink: {
        id: 'languageStudio.prebuiltDemosSection.customTranslationCardLink',
        defaultMessage: 'Open Custom translation'
    },
    taForHealthCardDescription: {
        id: 'languageStudio.prebuiltDemosSection.taForHealthCardDescription',
        defaultMessage: 'Extract and label relevant health information from unstructured text'
    },
    documentTranslationDemoName: {
        id: 'languageStudio.prebuiltDemosSection.documentTranslationDemoName',
        defaultMessage: 'Try document translation'
    },
    documentTranslationDemoDescription: {
        id: 'languageStudio.prebuiltDemosSection.documentTranslationDemoDescription',
        defaultMessage: 'Get multi-language support for document translation.'
    },
    summarizationDemoConversationDescription: {
        id: 'languageStudio.prebuiltDemosSection.summarizationDemoConversationDescription',
        defaultMessage: 'Summarize the most important or relevant information within documental and conversational text.'
    },
    callCenterDemoDescription: {
        id: 'languageStudio.prebuiltDemosSection.callCenterDemoDescription',
        defaultMessage: `Batch transcribe call center recordings and extract valuable information such as Personal Identifiable Information (PII), sentiment, and call summary.`
    },
    documentTranslationToolTitle: {
        id: 'languageStudio.prebuiltDemosSection.documentTranslationToolTitle',
        defaultMessage: 'Document translation'
    },
    documentTranslationToolDescription: {
        id: 'languageStudio.prebuiltDemosSection.documentTranslationToolDescription',
        defaultMessage: 'Batch translate documents into one or more languages either from local storage or Azure Blob Storage.'
    },
    documentTranslationToolCardLink: {
        id: 'languageStudio.prebuiltDemosSection.documentTranslationToolCardLink',
        defaultMessage: 'Translate documents'
    },
    customSentimentCardTitle: {
        id: 'languageStudio.prebuiltDemosSection.customSentimentCardTitle',
        defaultMessage: 'Custom sentiment analysis'
    },
    customSentimentCardDescription: {
        id: 'languageStudio.prebuiltDemosSection.customSentimentCardDescription',
        defaultMessage: 'Train a sentiment analysis model to detect sentiment in text using your own data'
    },
    customSentimentCardLink: {
        id: 'languageStudio.prebuiltDemosSection.customSentimentCardLink',
        defaultMessage: 'Open custom sentiment analysis'
    },
    customSummarizationCardTitle: {
        id: 'languageStudio.prebuiltDemosSection.customSummarizationCardTitle',
        defaultMessage: 'Custom summarization'
    },
    customSummarizationCardDescription: {
        id: 'languageStudio.prebuiltDemosSection.customSummarizationCardDescription',
        defaultMessage:
            'With Custom Summarization, you can build custom AI models to summarize your domain-specific documents. By creating a Custom Summarization project, you can iteratively label data, train, evaluate, and improve model performance before deploying your model and making it available for consumption.'
    },
    customSummarizationCardLink: {
        id: 'languageStudio.prebuiltDemosSection.customSummarizationCardLink',
        defaultMessage: 'Open custom summarization'
    },
    customHealthcareTooltip: {
        id: 'languageStudio.prebuiltDemosSection.customHealthcareTooltip',
        defaultMessage: 'Custom Text Analytics for health is currently only supported in UK South, East US, and North Europe regions.'
    },
    customSummarizationTooltip: {
        id: 'languageStudio.prebuiltDemosSection.customSummarizationTooltip',
        defaultMessage: 'Custom summarization is currently only supported in East US region.'
    },
    customClassificationTooltip: {
        id: 'languageStudio.prebuiltDemosSection.customClassificationTooltip',
        defaultMessage: 'Custom classification is currently only supported in certain regions.'
    },
    customSentimentTooltip: {
        id: 'languageStudio.prebuiltDemosSection.customSentimentTooltip',
        defaultMessage: 'Custom sentiment is currently only supported in certain regions.'
    },
    customConversationTooltip: {
        id: 'languageStudio.prebuiltDemosSection.customConversationTooltip',
        defaultMessage: 'Custom conversation is currently only supported in certain regions.'
    },
    customOrchestrationTooltip: {
        id: 'languageStudio.prebuiltDemosSection.customOrchestrationTooltip',
        defaultMessage: 'Custom orchestration is currently only supported in certain regions.'
    }
});

/**
 * Displays the demos section in the language studio landing page.
 */
export const PrebuiltDemosSection = observer((props: PrebuiltDemosSectionProps) => {
    const {
        id = 'demo',
        demosToDisplay = [
            'ner',
            'pii',
            'sentiment',
            'keyPhrases',
            'detectLanguage',
            'linkedEntities',
            'questionsAnswering',
            'generateQuestions',
            'summarization'
        ],
        demoTitle
    } = props;
    const { rootStore } = useGlobalState();
    const [isAzureCsSelectionModalHidden, setIsAzureCsSelectionModalHidden] = React.useState(true);
    const currentLanguageResource = rootStore.azureStore.session.csAccountStore;

    const isFeatureDisabled = (feature: string) => {
        const isDevOrStaging = isDevelopmentEnvironment() || isStagingEnvironment();
        switch (feature) {
            case 'customHealthcare':
                return !isDevOrStaging && currentLanguageResource && !currentLanguageResource.isCustomHealthcareCompatibleResource;
            case 'customSummarization':
                return !isDevOrStaging && currentLanguageResource && !currentLanguageResource.isSummarizationCompatibleResource;
            case 'customClassification':
            case 'customSentiment':
                return !isDevOrStaging && currentLanguageResource && !currentLanguageResource.isCustomTextCompatibleResource;
            case 'customConversation':
            case 'customOrchestration':
                return !isDevOrStaging && currentLanguageResource && !currentLanguageResource.isCluCompatibleResource;
            default:
                return true;
        }
    };

    const { localize } = useLocalization();

    const demosList: PrebuiltDemoCardProps[] = [
        {
            id: 'ner',
            cardType: 'prebuilt',
            featureName: 'Entity Extraction',
            imageSrc: getAssetsUrl(getImageSrc('nerDemoLogo')),
            demoName: localize(messages.identifyNamedEntitiesDemoName),
            demoLink: routeTable.languageStudio.namedEntitiesDemoPage(),
            description: localize(messages.identifyNamedEntitiesDemoDescription),
            onDemoClicked: () => demoTitle && logTileCardClick(demoTitle, 'Entity Extraction')
        },
        {
            id: 'sentiment',
            cardType: 'prebuilt',
            featureName: 'Analyze Sentiment',
            demoName: localize(messages.sentimentDemoName),
            demoLink: routeTable.languageStudio.sentimentDemoPage(),
            imageSrc: getAssetsUrl(getImageSrc('sentimentDemoLogo')),
            description: localize(messages.sentimentDemoDescription),
            onDemoClicked: () => demoTitle && logTileCardClick(demoTitle, 'Analyze Sentiment')
        },
        {
            id: 'detectLanguage',
            cardType: 'prebuilt',
            featureName: 'Language Detection',
            demoName: localize(messages.detectLanguageDemoName),
            demoLink: routeTable.languageStudio.detectLanguageDemoPage(),
            description: localize(messages.detectLanguageDemoDescription),
            imageSrc: getAssetsUrl(getImageSrc('detectLanguageDemoLogo')),
            onDemoClicked: () => demoTitle && logTileCardClick(demoTitle, 'Language Detection')
        },
        {
            id: 'questionsAnswering',
            cardType: 'prebuilt',
            featureName: 'Question answering',
            demoName: localize(messages.qnaDemoName),
            description: localize(messages.qnaDemoDescription),
            demoLink: routeTable.languageStudio.questionAnswersDemoPage(),
            imageSrc: getAssetsUrl(getImageSrc('questionsAnsweringDemoLogo')),
            onDemoClicked: () => demoTitle && logTileCardClick(demoTitle, 'Question answering')
        },
        {
            demoType: 'preview',
            id: 'generateQuestions',
            cardType: 'prebuilt',
            featureName: 'Generate questions',
            demoName: localize(messages.generateQuestionsDemoName),
            description: localize(messages.generateQuestionsDemoDescription),
            demoLink: routeTable.languageStudio.generateQuestionsDemoPage(),
            imageSrc: getAssetsUrl(getImageSrc('questionGenerationDemoLogo'))
        },
        {
            id: 'pii',
            cardType: 'prebuilt',
            featureName: 'Extract PII',
            demoName: localize(messages.piiDemoName),
            demoLink: routeTable.languageStudio.piiDemoPage(),
            description: localize(messages.piiDemoDescription),
            imageSrc: getAssetsUrl(getImageSrc('piiDemoLogo')),
            onDemoClicked: () => demoTitle && logTileCardClick(demoTitle, 'Extract PII')
        },
        {
            id: 'keyPhrases',
            cardType: 'prebuilt',
            featureName: 'Key Phrases',
            demoName: localize(messages.keyPhrasesDemoName),
            demoLink: routeTable.languageStudio.keyPhrasesDemoPage(),
            description: localize(messages.keyPhrasesDemoDescription),
            imageSrc: getAssetsUrl(getImageSrc('keyPhrasesDemoLogo')),
            onDemoClicked: () => demoTitle && logTileCardClick(demoTitle, 'Key Phrases')
        },
        {
            id: 'linkedEntities',
            cardType: 'prebuilt',
            featureName: 'Linked Entities',
            demoName: localize(messages.linkedEntitiesDemoName),
            demoLink: routeTable.languageStudio.linkedEntitiesDemoPage(),
            description: localize(messages.linkedEntitiesDemoDescription),
            imageSrc: getAssetsUrl(getImageSrc('linkedEntitiesDemoLogo')),
            onDemoClicked: () => demoTitle && logTileCardClick(demoTitle, 'Linked Entities')
        },
        {
            cardType: 'custom',
            featureName: 'ner',
            id: 'customEntityExtraction',
            demoLink: routeTable.customText.extractionProjectsPage(),
            demoName: localize(messages.customEntityExtractionCardTitle),
            tryOutMessage: localize(messages.customEntityExtractionCardLink),
            imageSrc: getAssetsUrl(getImageSrc('customEntityExtractionLogo')),
            description: localize(messages.customEntityExtractionCardDescription),
            onDemoClicked: () => demoTitle && logTileCardClick(demoTitle, 'ner')
        },
        {
            cardType: 'custom',
            featureName: 'customHealthcare',
            id: 'customHealthcare',
            demoLink: routeTable.customHealthcare.projectsPage(),
            demoName: localize(messages.customHealthCareCardTitle),
            tryOutMessage: localize(messages.customHealthCareCardLink),
            imageSrc: getAssetsUrl(getImageSrc('customHealthcare')),
            description: localize(messages.customHealthCareCardDescription),
            onDemoClicked: () => demoTitle && logTileCardClick(demoTitle, 'customHealthcare'),
            disabled: isFeatureDisabled('customHealthcare'),
            tooltipMessage: isFeatureDisabled('customHealthcare') && localize(messages.customHealthcareTooltip)
        },
        {
            cardType: 'custom',
            id: 'customClassification',
            featureName: 'classification',
            demoName: localize(messages.customClassificationCardTitle),
            demoLink: routeTable.customText.classificationProjectsPage(),
            tryOutMessage: localize(messages.customClassificationCardLink),
            imageSrc: getAssetsUrl(getImageSrc('customClassificationLogo')),
            description: localize(messages.customClassificationCardDescription),
            onDemoClicked: () => demoTitle && logTileCardClick(demoTitle, 'classification'),
            disabled: isFeatureDisabled('customClassification'),
            tooltipMessage: isFeatureDisabled('customClassification') && localize(messages.customClassificationTooltip)
        },
        {
            cardType: 'custom',
            id: 'customSentiment',
            featureName: 'customSentiment',
            demoName: localize(messages.customSentimentCardTitle),
            demoLink: routeTable.customSentiment.projectsPage(),
            tryOutMessage: localize(messages.customSentimentCardLink),
            imageSrc: getAssetsUrl(getImageSrc('customSentimentLogo')),
            description: localize(messages.customSentimentCardDescription),
            onDemoClicked: () => demoTitle && logTileCardClick(demoTitle, 'customSentiment'),
            disabled: isFeatureDisabled('customSentiment'),
            tooltipMessage: isFeatureDisabled('customSentiment') && localize(messages.customSentimentTooltip)
        },
        {
            featureName: 'qnaMaker',
            cardType: 'custom',
            id: 'customQuestionsAnswering',
            demoLink: routeTable.qnaMaker.projectsPage(),
            demoName: localize(messages.customQuestionsAnsweringCardTitle),
            subtitle: localize(messages.customQuestionsAnsweringCardSubtitle),
            tryOutMessage: localize(messages.customQuestionsAnsweringCardLink),
            imageSrc: getAssetsUrl(getImageSrc('customQuestionsAnsweringLogo')),
            description: localize(messages.customQuestionsAnsweringCardDescription),
            onDemoClicked: () => demoTitle && logTileCardClick(demoTitle, 'qnaMaker')
        },
        {
            cardType: 'custom',
            id: 'customTranslation',
            isExternalLinkDemo: true,
            featureName: 'translation',
            demoLink: customTranslationDemoLink,
            demoName: localize(messages.customTranslationCardTitle),
            tryOutMessage: localize(messages.customTranslationCardLink),
            imageSrc: getAssetsUrl(getImageSrc('customTranslationLogo')),
            description: localize(messages.customTranslationCardDescription),
            onDemoClicked: () => demoTitle && logTileCardClick(demoTitle, 'translation')
        },
        {
            cardType: 'custom',
            featureName: 'clu',
            id: 'customConversation',
            demoLink: routeTable.clu.root(),
            demoName: localize(messages.customConversationCardTitle),
            subtitle: localize(messages.customConversationCardSubtitle),
            tryOutMessage: localize(messages.customConversationCardLink),
            imageSrc: getAssetsUrl(getImageSrc('customConversationLogo')),
            description: localize(messages.customConversationCardDescription),
            onDemoClicked: () => demoTitle && logTileCardClick(demoTitle, 'clu'),
            disabled: isFeatureDisabled('customConversation'),
            tooltipMessage: isFeatureDisabled('customConversation') && localize(messages.customConversationTooltip)
        },
        {
            cardType: 'custom',
            id: 'customOrchestration',
            featureName: 'orchestration',
            demoLink: routeTable.orchestration.root(),
            demoName: localize(messages.customOrchestrationCardTitle),
            tryOutMessage: localize(messages.customOrchestrationCardLink),
            imageSrc: getAssetsUrl(getImageSrc('customOrchestrationLogo')),
            description: localize(messages.customOrchestrationCardDescription),
            onDemoClicked: () => demoTitle && logTileCardClick(demoTitle, 'orchestration'),
            disabled: isFeatureDisabled('customOrchestration'),
            tooltipMessage: isFeatureDisabled('customOrchestration') && localize(messages.customOrchestrationTooltip)
        },
        {
            cardType: 'prebuilt',
            id: 'textTranslation',
            featureName: 'Translate text',
            demoName: localize(messages.textTranslationDemoName),
            demoLink: routeTable.languageStudio.textTranslationDemoPage(),
            description: localize(messages.textTranslationDemoDescription),
            imageSrc: getAssetsUrl(getImageSrc('textTranslationDemoLogo')),
            onDemoClicked: () => demoTitle && logTileCardClick(demoTitle, 'Translate text')
        },
        {
            cardType: 'prebuilt',
            id: 'documentTranslation',
            featureName: 'document translation',
            demoName: localize(messages.documentTranslationDemoName),
            demoLink: routeTable.languageStudio.documentTranslationDemoPage(),
            description: localize(messages.documentTranslationDemoDescription),
            imageSrc: getAssetsUrl(getImageSrc('documentTranslationDemoLogo')),
            onDemoClicked: () => demoTitle && logTileCardClick(demoTitle, 'document translation demo')
        },
        {
            cardType: 'custom',
            demoType: 'preview',
            id: 'documentTranslationTool',
            featureName: 'document translation',
            demoName: localize(messages.documentTranslationToolTitle),
            demoLink: routeTable.translation.root(),
            description: localize(messages.documentTranslationToolDescription),
            imageSrc: getAssetsUrl(getImageSrc('documentTranslationToolLogo')),
            tryOutMessage: localize(messages.documentTranslationToolCardLink),
            onDemoClicked: () => demoTitle && logTileCardClick(demoTitle, 'document translation')
        },
        {
            id: 'taForHealth',
            cardType: 'prebuilt',
            featureName: 'Health information',
            demoName: localize(messages.taForHealthCardTitle),
            demoLink: routeTable.languageStudio.taForHealthDemoPage(),
            description: localize(messages.taForHealthCardDescription),
            imageSrc: getAssetsUrl(getImageSrc('taForHealthDemoLogo')),
            onDemoClicked: () => demoTitle && logTileCardClick(demoTitle, 'Health information')
        },
        {
            id: 'summarization',
            cardType: 'prebuilt',
            featureName: 'Summarization information',
            demoName: localize(messages.summarizationDemoName),
            demoLink: routeTable.languageStudio.summarizationDemoPage(),
            imageSrc: getAssetsUrl(getImageSrc('summarizationDemoLogo')),
            description: localize(messages.summarizationDemoConversationDescription),
            onDemoClicked: () => demoTitle && logTileCardClick(demoTitle, 'Summarization information')
        },
        {
            id: 'callCenter',
            demoType: 'preview',
            cardType: 'scenario',
            featureName: 'Call center',
            demoName: localize(messages.callCenterDemoName),
            tryOutMessage: localize(messages.callCenterCardLink),
            demoLink: routeTable.languageStudio.callCenterDemoPage(),
            description: localize(messages.callCenterDemoDescription),
            imageSrc: getAssetsUrl(getImageSrc('callCenterDemoLogo', 'jpg')),
            onDemoClicked: () => demoTitle && logTileCardClick(demoTitle, 'Call center')
        },
        {
            demoType: 'preview',
            cardType: 'custom',
            id: 'customSummarization',
            featureName: 'summarization',
            demoLink: routeTable.summarization.root(),
            demoName: localize(messages.customSummarizationCardTitle),
            tryOutMessage: localize(messages.customSummarizationCardLink),
            imageSrc: getAssetsUrl(getImageSrc('customSummarizationLogo')),
            description: localize(messages.customSummarizationCardDescription),
            onDemoClicked: () => demoTitle && logTileCardClick(demoTitle, 'customSummarization'),
            disabled: isFeatureDisabled('customSummarization'),
            tooltipMessage: isFeatureDisabled('customSummarization') && localize(messages.customSummarizationTooltip)
        }
    ];

    return (
        <>
            <Stack id={id} horizontal wrap tokens={{ childrenGap: 20 }}>
                {demosToDisplay
                    .map(demoToDisplay => demosList.find(d => d.id === demoToDisplay))
                    .map(demo => (
                        <PrebuiltDemoCard key={demo.demoName} {...demo} />
                    ))}
            </Stack>

            <AzureResourcesManager
                serviceTarget="TextTranslation"
                isSelectResourceVisible={!isAzureCsSelectionModalHidden}
                strategies={{ resource: AzureResourcesStrategies.translatorResourceStrategy }}
                onChangeSelectResourceVisible={isVisible => setIsAzureCsSelectionModalHidden(!isVisible)}
            />
        </>
    );
});
